// IMPORTS
import React, { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Papa from "papaparse";
import "./admin.css";
const supabaseUrl = "https://htcvvwvvlixmatxqpvyj.supabase.co";
const serviceRoleKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0Y3Z2d3Z2bGl4bWF0eHFwdnlqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc1MjgzMDc1MSwiZXhwIjoyMDY4NDA2NzUxfQ.YjJQn4yjvAzzWy3ibYvUJXjP1yPCFgEeNWTI5OFE6oo"; // Replace this securely in production

// SUPABASE INIT
const supabase = createClient(supabaseUrl, serviceRoleKey);

// RANDOM PASSWORD GENERATOR
function generateRandomPassword(length = 10) {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "student",
  });
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    teacher_ids: [],
  });
  const [enrollments, setEnrollments] = useState({
    course_id: "",
    student_id: "",
  });
  const [credentials, setCredentials] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [customPassword, setCustomPassword] = useState("");
  const [editingField, setEditingField] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editableCsvRows, setEditableCsvRows] = useState([
    { name: "", email: "", role: "", password: "" },
  ]);
  const [stagedEdits, setStagedEdits] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
    fetchCourses();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  const fetchUsers = async () => {
    const { data } = await supabase.from("users").select("*");
    const sorted = (data || []).sort((a, b) => a.name.localeCompare(b.name));
    setUsers(sorted);
  };

  const fetchCourses = async () => {
    const { data } = await supabase
      .from("courses")
      .select(
        `
        id,
        title,
        description,
        course_teachers (
          teacher_id,
          users ( name )
        )
      `
      )
      .order("title", { ascending: true });
    setCourses(data || []);
  };
  const updateCsvRow = (index, field, value) => {
    const updated = [...editableCsvRows];
    updated[index][field] = value;
    setEditableCsvRows(updated);
  };

  const addCsvRow = () => {
    setEditableCsvRows([
      ...editableCsvRows,
      { name: "", email: "", role: "", password: "" },
    ]);
  };

  const removeCsvRow = (index) => {
    const updated = editableCsvRows.filter((_, i) => i !== index);
    setEditableCsvRows(updated);
  };
  const submitCsvRows = async () => {
    const total = editableCsvRows.length;
    let completed = 0;
    setUploadProgress(0);

    for (const row of editableCsvRows) {
      const { name, email, role, password } = row;
      if (!name || !email || !role) {
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
        continue;
      }

      const { data: existing } = await supabase
        .from("users")
        .select("id")
        .eq("email", email);

      if (existing && existing.length > 0) {
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
        continue;
      }

      const finalPassword = password?.trim() || generateRandomPassword();

      const { data: authUser, error: authError } =
        await supabase.auth.admin.createUser({
          email,
          password: finalPassword,
          email_confirm: true,
          user_metadata: { full_name: name, role },
        });

      if (!authUser || authError) {
        completed++;
        setUploadProgress(Math.round((completed / total) * 100));
        continue;
      }

      await supabase.from("users").insert({
        id: authUser.user.id,
        name,
        email,
        role,
        plaintext_password: finalPassword,
        is_blocked: false,
        blocked_until: null,
      });

      completed++;
      setUploadProgress(Math.round((completed / total) * 100));
    }

    fetchUsers();
    alert("Users submitted successfully.");
    setUploadProgress(100);
  };

  const createUser = async () => {
    const { data: existingUsers } = await supabase
      .from("users")
      .select("id")
      .eq("email", newUser.email);

    if (existingUsers && existingUsers.length > 0) {
      alert("User with this email already exists.");
      return;
    }

    const password = autoGeneratePassword
      ? generateRandomPassword()
      : customPassword;
    if (!password) return alert("Please enter a password.");

    const { data: authUser, error: authError } =
      await supabase.auth.admin.createUser({
        email: newUser.email,
        password,
        email_confirm: true,
        user_metadata: { full_name: newUser.name, role: newUser.role },
      });
    if (authError) {
      alert("Auth creation failed: " + authError.message);
      return;
    }

    const userData = {
      id: authUser.user.id,
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      plaintext_password: password,
      is_blocked: false,
      blocked_until: null,
    };
    const { error: dbError } = await supabase.from("users").insert(userData);
    if (dbError) {
      alert("Database insertion failed: " + dbError.message);
      return;
    }

    setCredentials({ email: newUser.email, password });
    fetchUsers();
  };

  const handleCsvUpload = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const processCsv = async () => {
    if (!csvFile) return alert("Please upload a CSV file");
    setUploadProgress(0);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const total = results.data.length;
        let completed = 0;

        for (const row of results.data) {
          const { name, email, role, password: csvPassword } = row;
          const { data: existing } = await supabase
            .from("users")
            .select("id")
            .eq("email", email);

          if (existing && existing.length > 0) {
            completed++;
            setUploadProgress(Math.round((completed / total) * 100));
            continue;
          }

          const password = csvPassword?.trim() || generateRandomPassword();
          const { data: authUser, error: authError } =
            await supabase.auth.admin.createUser({
              email,
              password,
              email_confirm: true,
              user_metadata: { full_name: name, role },
            });

          if (!authUser || authError) {
            completed++;
            setUploadProgress(Math.round((completed / total) * 100));
            continue;
          }

          await supabase.from("users").insert({
            id: authUser.user.id,
            name,
            email,
            role,
            plaintext_password: password,
            is_blocked: false,
            blocked_until: null,
          });

          completed++;
          setUploadProgress(Math.round((completed / total) * 100));
        }

        fetchUsers();
        alert("CSV processed successfully. Users created.");
        setUploadProgress(100);
      },
    });
  };

  const saveUserField = async (id, field, value) => {
    if (!value) return;
    if (field === "plaintext_password") {
      await supabase.auth.admin.updateUserById(id, { password: value });
    }
    const { error } = await supabase
      .from("users")
      .update({ [field]: value })
      .eq("id", id);
    if (error) alert(error.message);
    setEditingField({});
    fetchUsers();
  };

  const createCourse = async () => {
    const { data: existingCourses } = await supabase
      .from("courses")
      .select("id")
      .eq("title", newCourse.title);

    if (existingCourses && existingCourses.length > 0) {
      alert("Course with this title already exists.");
      return;
    }

    const { data: insertedCourse, error: courseError } = await supabase
      .from("courses")
      .insert({ title: newCourse.title, description: newCourse.description })
      .select()
      .single();
    if (courseError) {
      alert(courseError.message);
      return;
    }

    const teacherRows = newCourse.teacher_ids.map((teacher_id) => ({
      course_id: insertedCourse.id,
      teacher_id,
    }));
    const { error: teacherLinkError } = await supabase
      .from("course_teachers")
      .insert(teacherRows);
    if (teacherLinkError) {
      alert(
        "Course created but assigning teachers failed: " +
          teacherLinkError.message
      );
    }
    fetchCourses();
  };

  const enrollStudent = async () => {
    const { data: existing } = await supabase
      .from("course_enrollments")
      .select("id")
      .eq("student_id", enrollments.student_id)
      .eq("course_id", enrollments.course_id);

    if (existing && existing.length > 0) {
      alert("This student is already enrolled in this course.");
      return;
    }

    const { error } = await supabase
      .from("course_enrollments")
      .insert(enrollments);
    if (!error) alert("Student Enrolled Successfully");
    else alert(error.message);
  };

  const toggleBlockUser = async (id, isBlocked, blockedUntil) => {
    if (isBlocked) {
      const confirmUnblock = window.confirm(
        "Do you want to unblock this user manually?"
      );
      if (!confirmUnblock) return;

      const { error: dbError } = await supabase
        .from("users")
        .update({ is_blocked: false, blocked_until: null })
        .eq("id", id);
      if (dbError) return alert(dbError.message);

      await supabase.auth.admin.updateUserById(id, { banned: false });
    } else {
      const duration = window.prompt(
        "Enter block duration in days (1-60):",
        "15"
      );
      const days = parseInt(duration, 10);
      if (isNaN(days) || days < 1 || days > 60)
        return alert("Invalid duration. Use a number between 1 and 60.");

      const until = new Date(Date.now() + days * 86400000).toISOString();
      const { error: dbError } = await supabase
        .from("users")
        .update({ is_blocked: true, blocked_until: until })
        .eq("id", id);
      if (dbError) return alert(dbError.message);

      await supabase.auth.admin.updateUserById(id, { banned: true });
    }
    fetchUsers();
  };

  const deleteUser = async (id) => {
    if (window.confirm("Delete user?")) {
      await supabase.from("users").delete().eq("id", id);
      await supabase.auth.admin.deleteUser(id);
      fetchUsers();
    }
  };

  const deleteCourse = async (id) => {
    if (window.confirm("Delete course?")) {
      await supabase.from("courses").delete().eq("id", id);
      fetchCourses();
    }
  };
  const saveUserEdits = async (userId) => {
    const edits = stagedEdits[userId];
    if (!edits) return;

    if (edits.plaintext_password) {
      await supabase.auth.admin.updateUserById(userId, {
        password: edits.plaintext_password,
      });
    }

    const { error } = await supabase
      .from("users")
      .update(edits)
      .eq("id", userId);

    if (error) {
      alert(error.message);
    } else {
      alert("Information saved successfully.");
      setStagedEdits((prev) => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      setEditingField({});
      fetchUsers();
    }
  };

  return (
    <div className="admin-dashboard">
      <div
        className="dashboard-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1>Admin Dashboard</h1>
        <button
          onClick={handleLogout}
          style={{
            padding: "8px 12px",
            background: "#e53e3e",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Logout
        </button>
      </div>

      <section>
        <h2>Create User</h2>
        <input
          placeholder="Name"
          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
        />
        <input
          placeholder="Email"
          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
        />
        <select
          onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
        >
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
          <option value="admin">Admin</option>
        </select>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            marginTop: "12px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={autoGeneratePassword}
              onChange={(e) => setAutoGeneratePassword(e.target.checked)}
              style={{ marginRight: "8px" }}
            />
            Auto-generate password
          </label>
        </div>

        {!autoGeneratePassword && (
          <input
            placeholder="Enter Password"
            type="password"
            onChange={(e) => setCustomPassword(e.target.value)}
          />
        )}
        <button onClick={createUser}>Create User</button>
        {credentials && (
          <div>
            <p>Credentials:</p>
            <p>Email: {credentials.email}</p>
            <p>Password: {credentials.password}</p>
          </div>
        )}
      </section>

      <section>
        <h2>Bulk Upload Users via Table</h2>
        <h3>
          (name, email, role [student/teacher/admin], password is optional)
        </h3>

        {/* Editable Table */}
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Password</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {editableCsvRows.map((row, index) => (
              <tr key={index}>
                <td>
                  <input
                    value={row.name}
                    onChange={(e) =>
                      updateCsvRow(index, "name", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    value={row.email}
                    onChange={(e) =>
                      updateCsvRow(index, "email", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    value={row.role}
                    onChange={(e) =>
                      updateCsvRow(index, "role", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <input
                    value={row.password}
                    onChange={(e) =>
                      updateCsvRow(index, "password", e.target.value)
                    }
                    placeholder="Leave blank to auto-generate"
                  />
                </td>
                <td>
                  <button onClick={() => removeCsvRow(index)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "10px" }}>
          <button onClick={addCsvRow}>Add Row</button>
          <button onClick={submitCsvRows} style={{ marginLeft: "10px" }}>
            Submit Users
          </button>
        </div>

        {uploadProgress > 0 && (
          <div style={{ marginTop: "10px", width: "100%" }}>
            <progress
              value={uploadProgress}
              max="100"
              style={{ width: "100%" }}
            />
            <p>{uploadProgress}% uploaded</p>
          </div>
        )}
      </section>

      <section>
        <h2>All Users</h2>
        <input
          type="text"
          placeholder="Search users by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            marginBottom: "10px",
            padding: "8px",
            width: "100%",
            maxWidth: "400px",
            fontSize: "16px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Blocked</th>
              <th>Blocked Until</th>
              <th>Password</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((user) =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((user) => {
                const userEdits = stagedEdits[user.id] || {};
                const hasEdits = Object.keys(userEdits).length > 0;

                return (
                  <tr key={user.id}>
                    <td>
                      <input
                        value={userEdits.name ?? user.name}
                        onChange={(e) =>
                          setStagedEdits((prev) => ({
                            ...prev,
                            [user.id]: {
                              ...prev[user.id],
                              name: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={userEdits.email ?? user.email}
                        onChange={(e) =>
                          setStagedEdits((prev) => ({
                            ...prev,
                            [user.id]: {
                              ...prev[user.id],
                              email: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>{user.role}</td>
                    <td>{user.is_blocked ? "Yes" : "No"}</td>
                    <td>
                      {user.blocked_until
                        ? new Date(user.blocked_until).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <input
                        value={
                          userEdits.plaintext_password ??
                          user.plaintext_password
                        }
                        onChange={(e) =>
                          setStagedEdits((prev) => ({
                            ...prev,
                            [user.id]: {
                              ...prev[user.id],
                              plaintext_password: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <button
                        onClick={() =>
                          toggleBlockUser(
                            user.id,
                            user.is_blocked,
                            user.blocked_until
                          )
                        }
                      >
                        {user.is_blocked ? "Unblock/Extend" : "Block"}
                      </button>
                      <button onClick={() => deleteUser(user.id)}>
                        Delete
                      </button>
                      {hasEdits && (
                        <button
                          onClick={() => saveUserEdits(user.id)}
                          style={{
                            marginLeft: "6px",
                            backgroundColor: "#3182ce",
                            color: "#fff",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                          }}
                        >
                          Save Info
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Create Course</h2>
        <input
          placeholder="Title"
          onChange={(e) =>
            setNewCourse({ ...newCourse, title: e.target.value })
          }
        />
        <input
          placeholder="Description"
          onChange={(e) =>
            setNewCourse({ ...newCourse, description: e.target.value })
          }
        />
        <Select
          isMulti
          options={users
            .filter((u) => u.role === "teacher")
            .map((t) => ({ value: t.id, label: t.name }))}
          onChange={(opts) =>
            setNewCourse({
              ...newCourse,
              teacher_ids: opts.map((o) => o.value),
            })
          }
          placeholder="Select teacher(s)..."
        />
        <button onClick={createCourse}>Create Course</button>
      </section>

      <section>
        <h2>Enroll Student</h2>
        <select
          onChange={(e) =>
            setEnrollments({ ...enrollments, student_id: e.target.value })
          }
        >
          <option value="">Select Student</option>
          {users
            .filter((u) => u.role === "student")
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
        <select
          onChange={(e) =>
            setEnrollments({ ...enrollments, course_id: e.target.value })
          }
        >
          <option value="">Select Course</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <button onClick={enrollStudent}>Enroll</button>
      </section>

      <section>
        <h2>All Courses</h2>
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Description</th>
              <th>Teacher(s)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id}>
                <td>{c.title}</td>
                <td>{c.description}</td>
                <td>
                  {(c.course_teachers || [])
                    .map((ct) => ct.users?.name)
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </td>
                <td>
                  <button onClick={() => deleteCourse(c.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AdminDashboard;
